import { type KeyboardEvent, type ReactNode, useCallback, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from './icons';
import { Popover } from './Popover';

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  /** Colour dot (statuses, priorities, projects). */
  color?: string;
  /** Avatar initials (people). */
  avatar?: string;
  avatarColor?: string;
  desc?: string;
  group?: string;
  disabled?: boolean;
}

interface BaseProps<V extends string> {
  options: SelectOption<V>[];
  placeholder?: string;
  'aria-label'?: string;
  id?: string;
  searchable?: boolean;
  clearable?: boolean;
  size?: 'md' | 'sm';
  bare?: boolean;
  disabled?: boolean;
  /** Mobile sheet title. */
  title?: string;
}
export interface SingleSelectProps<V extends string> extends BaseProps<V> {
  multiple?: false;
  value: V | null;
  onChange: (value: V | null) => void;
}
export interface MultiSelectProps<V extends string> extends BaseProps<V> {
  multiple: true;
  value: V[];
  onChange: (value: V[]) => void;
}
export type SelectProps<V extends string> = SingleSelectProps<V> | MultiSelectProps<V>;

function Lead({ o }: { o: SelectOption }): ReactNode {
  if (o.color) return <span className="ui-dot" style={{ background: o.color }} />;
  if (o.avatar) return <span className="ui-av" style={o.avatarColor ? { background: o.avatarColor } : undefined}>{o.avatar}</span>;
  return null;
}

/** Custom dropdown: single/multi, searchable, grouped, keyboard (↑↓ Home End Enter Esc), bottom sheet on phones. */
export function Select<V extends string>(props: SelectProps<V>) {
  const { options, placeholder = 'Select…', searchable, clearable, size = 'md', bare, disabled, title } = props;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);

  const selected = useMemo(() => new Set<V>(props.multiple ? props.value : props.value ? [props.value] : []), [props.multiple, props.value]);
  const showSearch = searchable ?? options.length > 8;
  const filtered = useMemo(() => options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())), [options, q]);
  const close = useCallback(() => {
    setOpen(false);
    setQ('');
  }, []);

  const pick = (o: SelectOption<V>) => {
    if (o.disabled) return;
    if (props.multiple) {
      const next = selected.has(o.value) ? props.value.filter((v) => v !== o.value) : [...props.value, o.value];
      props.onChange(next);
    } else {
      props.onChange(o.value);
      close();
      triggerRef.current?.focus();
    }
  };

  const openList = () => {
    const i = filtered.findIndex((o) => selected.has(o.value));
    setActive(Math.max(0, i));
    setOpen(true);
  };

  const onKey = (e: KeyboardEvent) => {
    const n = filtered.length;
    if (!n) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % n); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + n) % n); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(n - 1); }
    else if (e.key === 'Enter' || (e.key === ' ' && !showSearch)) {
      e.preventDefault();
      const o = filtered[active];
      if (o) pick(o);
    } else if (e.key === 'Tab') close();
  };

  const chosen = options.filter((o) => selected.has(o.value));
  const cls = ['ui-trigger', size === 'sm' && 'sm', bare && 'bare'].filter(Boolean).join(' ');
  let group: string | undefined;

  return (
    <>
      <button
        ref={triggerRef}
        id={props.id}
        type="button"
        className={cls}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={props['aria-label']}
        onClick={() => (open ? close() : openList())}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); openList(); }
        }}
      >
        <span className="ui-val">
          {chosen.length === 0 ? (
            <span className="ui-ph">{placeholder}</span>
          ) : props.multiple ? (
            <span className="ui-chips">
              {chosen.slice(0, 3).map((o) => (
                <span key={o.value} className="ui-chip"><Lead o={o} /><span>{o.label}</span></span>
              ))}
              {chosen.length > 3 && <span className="ui-chip">+{chosen.length - 3}</span>}
            </span>
          ) : (
            <><Lead o={chosen[0]!} /><span>{chosen[0]!.label}</span></>
          )}
        </span>
        {clearable && chosen.length > 0 && (
          <span
            className="ui-clear"
            role="button"
            aria-label="Clear"
            onClick={(e) => {
              e.stopPropagation();
              if (props.multiple) props.onChange([]);
              else props.onChange(null);
            }}
          >
            <X />
          </span>
        )}
        <span className="ui-chev"><ChevronDown /></span>
      </button>
      <Popover
        anchorRef={triggerRef}
        open={open}
        onClose={close}
        role="listbox"
        title={title ?? props['aria-label'] ?? placeholder}
        footer={props.multiple ? <div className="ui-sheet-foot"><button type="button" className="ui-btn primary" onClick={close}>Done</button></div> : undefined}
      >
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          {showSearch && (
            <div className="ui-search">
              <input autoFocus value={q} placeholder="Search…" aria-label="Search options" autoComplete="off" onChange={(e) => { setQ(e.target.value); setActive(0); }} onKeyDown={onKey} />
            </div>
          )}
          <ul id={listId} className="ui-list" role="listbox" aria-multiselectable={props.multiple || undefined} tabIndex={showSearch ? -1 : 0} autoFocus={!showSearch} onKeyDown={showSearch ? undefined : onKey}>
            {filtered.length === 0 && <li className="ui-empty">No matches for “{q}”</li>}
            {filtered.map((o, i) => {
              const head = o.group && o.group !== group ? (group = o.group) : null;
              const isSel = selected.has(o.value);
              return [
                head && <li key={`g-${head}`} className="ui-group" role="presentation">{head}</li>,
                <li
                  key={o.value}
                  role="option"
                  aria-selected={isSel}
                  aria-disabled={o.disabled || undefined}
                  className={`ui-opt${i === active ? ' is-active' : ''}`}
                  onPointerMove={() => setActive(i)}
                  onClick={() => pick(o)}
                >
                  {props.multiple && <span className="ui-box">{isSel && <Check size={12} />}</span>}
                  <Lead o={o} />
                  <span className="ui-opt-text"><b>{o.label}</b>{o.desc && <small>{o.desc}</small>}</span>
                  {!props.multiple && <span className="ui-check"><Check /></span>}
                </li>,
              ];
            })}
          </ul>
        </div>
      </Popover>
    </>
  );
}
